import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, Mail, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import VerificarStatusClicksign from './VerificarStatusClicksign';

interface Contrato {
  id: string;
  documento_cpf_nome: string;
  clicksign_assinado: any; // Can be number, boolean, or string from Supabase
}

interface ContratoStatusDisplayProps {
  userEmail: string;
  userId: string;
  onComplete: () => void;
}

export default function ContratoStatusDisplay({ userEmail, userId, onComplete }: ContratoStatusDisplayProps) {
  const [loading, setLoading] = useState(true);
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [statusGeral, setStatusGeral] = useState<'pendente' | 'concluido' | 'nao_encontrado'>('nao_encontrado');
  const [contratacaoId, setContratacaoId] = useState<string>('');
  const { toast } = useToast();

  // Função auxiliar para verificar se está assinado
  const isAssinado = (value: any): boolean => {
    return value === 1 || value === true || value === '1' || value === 'true';
  };

  // Função auxiliar para verificar se está pendente
  const isPendente = (value: any): boolean => {
    return value === 'pendente' || value === 0 || value === false || value === '0' || value === 'false';
  };

  useEffect(() => {
    if (userEmail) {
      buscarContratacaoEVerificarContratos();
    }
  }, [userEmail]);

  const buscarContratacaoEVerificarContratos = async () => {
    try {
      setLoading(true);
      
      // Primeiro, buscar a contratação pelo email
      const { data: contratacao, error: contratacaoError } = await supabase
        .from('contratacao')
        .select('id')
        .eq('email', userEmail)
        .maybeSingle();

      if (contratacaoError) {
        console.error('Erro ao buscar contratação:', contratacaoError);
        toast({
          title: "Erro",
          description: "Erro ao buscar dados de contratação.",
          variant: "destructive"
        });
        setStatusGeral('nao_encontrado');
        return;
      }

      if (!contratacao) {
        setStatusGeral('nao_encontrado');
        setContratos([]);
        return;
      }

      setContratacaoId(contratacao.id);

      // Buscar contratos na tabela contratacao_contratos
      const { data: contratos, error } = await supabase
        .from('contratacao_contratos')
        .select('id, documento_cpf_nome, clicksign_assinado')
        .eq('contratacao_id', contratacao.id)
        .like('documento_cpf_nome', 'Contrato_%');

      if (error) {
        console.error('Erro ao buscar contratos:', error);
        toast({
          title: "Erro",
          description: "Erro ao verificar status dos contratos.",
          variant: "destructive"
        });
        return;
      }

      if (!contratos || contratos.length === 0) {
        setStatusGeral('nao_encontrado');
        setContratos([]);
        return;
      }

      setContratos(contratos);

      // Verificar se todos os contratos estão assinados
      const todosAssinados = contratos.every(contrato => isAssinado(contrato.clicksign_assinado));
      const algumPendente = contratos.some(contrato => isPendente(contrato.clicksign_assinado));

      if (todosAssinados) {
        setStatusGeral('concluido');
      } else if (algumPendente) {
        setStatusGeral('pendente');
      } else {
        setStatusGeral('nao_encontrado');
      }

    } catch (error) {
      console.error('Erro ao verificar status dos contratos:', error);
      toast({
        title: "Erro",
        description: "Erro inesperado ao verificar contratos.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvancar = async () => {
    // Atualizar evolução para próxima etapa usando o user_id correto
    try {
      const { data: evolucaoExistente } = await supabase
        .from('evolucao_contratacao')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (evolucaoExistente) {
        await supabase
          .from('evolucao_contratacao')
          .update({ 
            etapa_atual: 'integracao',
            etapa_mais_avancada: 'integracao',
            contrato_assinado: true
          })
          .eq('user_id', userId);
      } else {
        await supabase
          .from('evolucao_contratacao')
          .insert({
            user_id: userId,
            etapa_atual: 'integracao',
            etapa_mais_avancada: 'integracao',
            contrato_assinado: true
          });
      }
      
      onComplete();
    } catch (error) {
      console.error('Erro ao avançar etapa:', error);
      toast({
        title: "Erro",
        description: "Erro ao avançar para próxima etapa.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Verificando status dos contratos...</p>
        </div>
      </div>
    );
  }

  const getModeloFromDocumento = (documento: string) => {
    const parts = documento.split('_');
    return parts.length > 1 ? parts[1] : 'N/A';
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl sm:text-2xl font-bold mb-2">Assinatura do Contrato</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Status dos seus contratos de trabalho
        </p>
      </div>

      <Card className="border-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {statusGeral === 'concluido' ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span>Contratos Assinados</span>
                <Badge variant="default" className="bg-green-100 text-green-800">
                  Concluído
                </Badge>
              </>
            ) : statusGeral === 'pendente' ? (
              <>
                <Clock className="h-5 w-5 text-amber-600" />
                <span>Contratos Enviados</span>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                  Aguardando Assinatura
                </Badge>
              </>
            ) : (
              <>
                <Clock className="h-5 w-5 text-gray-600" />
                <span>Contratos</span>
                <Badge variant="outline">
                  Não Encontrados
                </Badge>
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {statusGeral === 'concluido' ? (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-green-800 font-medium">
                    Assinatura do Contrato de Trabalho confirmada!
                  </p>
                  <p className="text-green-700 text-sm mt-1">
                    Você está liberado para avançar para a próxima etapa.
                  </p>
                </div>
              </div>
            </div>
          ) : statusGeral === 'pendente' ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-800 font-medium">
                    O seu Contrato de Trabalho foi enviado para o seu e-mail.
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    Verifique sua caixa de entrada e também a de spam para assiná-lo. 
                    Após a assinatura ser confirmada, a próxima etapa será liberada automaticamente.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-800 font-medium">
                    Nenhum contrato encontrado para este usuário.
                  </p>
                  <p className="text-gray-700 text-sm mt-1">
                    Entre em contato com o RH para verificar a situação.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Lista de contratos quando há mais de um */}
          {contratos.length > 1 && (
            <div className="mt-4">
              <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Seus Contratos ({contratos.length})
              </h4>
              <div className="space-y-2">
                {contratos.map((contrato, index) => (
                  <div 
                    key={contrato.id}
                    className="flex items-center justify-between p-3 bg-white border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">
                          Contrato {index + 1} - {getModeloFromDocumento(contrato.documento_cpf_nome)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {contrato.documento_cpf_nome}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant={isAssinado(contrato.clicksign_assinado) ? "default" : "secondary"}
                      className={
                        isAssinado(contrato.clicksign_assinado)
                          ? "bg-green-100 text-green-800" 
                          : "bg-amber-100 text-amber-800"
                      }
                    >
                      {isAssinado(contrato.clicksign_assinado) ? "Assinado" : "Pendente"}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {statusGeral === 'pendente' && (
            <VerificarStatusClicksign 
              onVerificacaoCompleta={() => {
                // Recarregar status após verificação
                setTimeout(() => {
                  buscarContratacaoEVerificarContratos();
                }, 2000);
              }} 
            />
          )}

          {statusGeral === 'concluido' && (
            <div className="flex justify-end pt-4">
              <Button onClick={handleAvancar} size="lg">
                Avançar para Próxima Etapa
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}